import React, { useEffect, useState } from "react";
import { Breadcrumbs } from "../AdminLayout";
import { adminApi, formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { UserCheck, Ban, RefreshCcw, Search, ShieldCheck } from "lucide-react";

const doctorAccountsApi = {
  list: (params = {}) => adminApi.get("/admin/doctor-accounts", { params }).then(r => r.data),
  counts: () => adminApi.get("/admin/doctor-accounts/counts").then(r => r.data),
  verify: (id, status, notes = "") =>
    adminApi.patch(`/admin/doctor-accounts/${id}/verify`, { status, notes }).then(r => r.data),
};

const STATUS_META = {
  pending:  { label: "Pending",  color: "text-[#D97706]", bg: "bg-[#D97706]/10" },
  verified: { label: "Verified", color: "text-[#3F6250]", bg: "bg-[#3F6250]/10" },
  rejected: { label: "Rejected", color: "text-[#9F1239]", bg: "bg-[#9F1239]/10" },
  revoked:  { label: "Revoked",  color: "text-stone2",     bg: "bg-stone2/10" },
};
const TABS = ["pending", "verified", "rejected", "revoked"];

export default function DoctorAccounts() {
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, verified: 0, rejected: 0, revoked: 0 });
  const [expanded, setExpanded] = useState(null);

  const reload = () => {
    doctorAccountsApi.list({ status: tab, q: q || undefined })
      .then(setRows).catch(e => toast.error(formatErr(e.response?.data?.detail)));
    doctorAccountsApi.counts().then(setCounts).catch(() => {});
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    const t = setTimeout(reload, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const act = async (row, newStatus) => {
    const notes = window.prompt(`Reviewer notes for ${newStatus} (optional)`, "") ?? "";
    try {
      await doctorAccountsApi.verify(row.id, newStatus, notes);
      toast.success(`Marked ${newStatus}`);
      setExpanded(null);
      reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="doctor-accounts">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Doctor Portal" }, { label: "Doctor Accounts" }]}/>
      <div className="flex items-center justify-between mt-4 mb-6 gap-4">
        <div className="font-serif text-3xl text-navy flex items-center gap-3"><ShieldCheck size={22}/> Doctor Accounts</div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone2"/>
          <input data-testid="da-search" value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Search email / name / institution…"
                 className="border border-line pl-9 pr-3 py-2 text-sm w-72"/>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-line mb-4">
        {TABS.map(t => (
          <button key={t} data-testid={`da-tab-${t}`} onClick={() => setTab(t)}
                  className={`mono text-[10px] uppercase tracking-widest px-4 py-2 border-b-2 -mb-px inline-flex items-center gap-2 ${tab === t ? "border-navy text-navy" : "border-transparent text-stone2 hover:text-navy"}`}>
            {STATUS_META[t].label}
            <span className="text-[10px] bg-line text-navy px-1.5 py-0.5">{counts[t] || 0}</span>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-line p-10 text-center text-sm text-stone2">No {STATUS_META[tab].label.toLowerCase()} doctor accounts.</div>
      ) : (
        <ul className="space-y-2">
          {rows.map(r => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const isOpen = expanded === r.id;
            return (
              <li key={r.id} data-testid={`da-row-${r.id}`} className="bg-white border border-line">
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-serif text-lg text-navy truncate">{r.name}</div>
                      <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${meta.bg} ${meta.color}`}>{meta.label}</span>
                      <span className="mono text-[10px] text-stone2">· {r.credential_type}</span>
                    </div>
                    <div className="mono text-[10px] text-stone2 truncate">{r.email} · {r.institution || "—"}</div>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <button data-testid={`da-toggle-${r.id}`} onClick={() => setExpanded(isOpen ? null : r.id)}
                            className="mono text-[10px] uppercase tracking-widest border border-line hover:border-navy text-navy px-3 py-1.5">
                      {isOpen ? "Hide" : "Details"}
                    </button>
                    {r.status !== "verified" && (
                      <button data-testid={`da-verify-${r.id}`} onClick={() => act(r, "verified")}
                              className="mono text-[10px] uppercase tracking-widest bg-[#3F6250] text-white hover:bg-[#3F6250]/85 px-3 py-1.5 inline-flex items-center gap-1">
                        <UserCheck size={11}/> Verify
                      </button>
                    )}
                    {r.status === "verified" && (
                      <button data-testid={`da-revoke-${r.id}`} onClick={() => act(r, "revoked")}
                              className="mono text-[10px] uppercase tracking-widest border border-[#9F1239] text-[#9F1239] hover:bg-[#9F1239] hover:text-white px-3 py-1.5 inline-flex items-center gap-1">
                        <Ban size={11}/> Revoke
                      </button>
                    )}
                    {r.status === "pending" && (
                      <button data-testid={`da-reject-${r.id}`} onClick={() => act(r, "rejected")}
                              className="mono text-[10px] uppercase tracking-widest text-[#9F1239] hover:underline px-2 py-1.5">
                        Reject
                      </button>
                    )}
                    {(r.status === "rejected" || r.status === "revoked") && (
                      <button data-testid={`da-restore-${r.id}`} onClick={() => act(r, "pending")}
                              className="mono text-[10px] uppercase tracking-widest border border-line hover:border-navy text-stone2 hover:text-navy px-3 py-1.5 inline-flex items-center gap-1">
                        <RefreshCcw size={11}/> Restore to pending
                      </button>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div data-testid={`da-details-${r.id}`} className="border-t border-line px-4 py-3 bg-clinical/40 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Licence no." value={r.licence_no}/>
                      <Field label="Phone" value={r.phone}/>
                      <Field label="Institution" value={r.institution}/>
                      <Field label="Credential" value={r.credential_type}/>
                      <Field label="Registered" value={r.created_at}/>
                      <Field label="Last login" value={r.last_login_at || "—"}/>
                    </div>
                    {r.notes && (<div className="mt-3"><div className="mono text-[9px] uppercase tracking-widest text-stone2">Applicant notes</div><div className="italic text-stone2">"{r.notes}"</div></div>)}
                    {r.reviewer_notes && (<div className="mt-3"><div className="mono text-[9px] uppercase tracking-widest text-stone2">Reviewer notes</div><div className="italic text-navy">"{r.reviewer_notes}"</div></div>)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-xs text-stone2 mt-6 border-t border-line pt-3">
        Every verify / reject / revoke is audit-logged. Rejecting or revoking a doctor also terminates their active sessions so outstanding tokens cannot access doctor-tier content.
      </p>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="mono text-[9px] uppercase tracking-widest text-stone2">{label}</div>
      <div className="text-navy truncate">{value || "—"}</div>
    </div>
  );
}
